import { EmptyState, Fieldset, Stack, Table, VStack } from '@chakra-ui/react'
import truncate from 'lodash/truncate'
import type { FC } from 'react'
import { useState } from 'react'

import { useColorModeValue } from '@app/components/ui/color-mode'
import type { DashboardItem } from '@app/pages/aiDashboard/types'
import MessageContent from '@app/pages/strategies/components/aiChat/components/messageContent'

interface Props {
  history: DashboardItem['reports']
}

const AiDashboardReportHistory: FC<Props> = ({ history }) => {
  const [report, setReport] = useState(history?.[0])
  const selectedRowColor = useColorModeValue('blue.50', 'blue.900')

  if (!history || !history?.length) {
    return (
      <EmptyState.Root>
        <EmptyState.Content>
          <VStack textAlign="center">
            <EmptyState.Title>No history</EmptyState.Title>
            <EmptyState.Description>No historical data is available for this report.</EmptyState.Description>
          </VStack>
        </EmptyState.Content>
      </EmptyState.Root>
    )
  }

  const handleRowClick = (id: number) => {
    setReport(history[id])
  }

  const blockMatch = (message: string) => ({
    block: null,
    priority: null,
    llmOutput: null,
    isComplete: true,
    startIndex: 0,
    endIndex: 0,
    outputRaw: null,
    visibleText: null,
    isVisible: true,
    output: message
  })

  return (
    <Stack gap={6}>
      <Table.ScrollArea borderWidth={1} rounded="md" maxHeight="250px">
        <Table.Root interactive stickyHeader>
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader>Summary</Table.ColumnHeader>
              <Table.ColumnHeader>Created at</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {history.map((historicalReport, idx) => {
              const rowProps = report?.id === historicalReport.id ? { bgColor: selectedRowColor } : {}

              return (
                <Table.Row key={historicalReport.id} onClick={() => handleRowClick(idx)} cursor="pointer" {...rowProps}>
                  <Table.Cell>{truncate(historicalReport.summary || 'No summary', { length: 50 })}</Table.Cell>
                  <Table.Cell>{new Date(historicalReport.createdAt).toLocaleString()}</Table.Cell>
                </Table.Row>
              )
            })}
          </Table.Body>
        </Table.Root>
      </Table.ScrollArea>
      <Fieldset.Root borderWidth={1} borderStyle="solid" borderColor="border.default" borderRadius="md" px={6} pb={6}>
        <Fieldset.Legend>Generated report</Fieldset.Legend>
        <Fieldset.Content>
          {report && <MessageContent blockMatch={blockMatch(report.summary || 'No summary')} />}
        </Fieldset.Content>
      </Fieldset.Root>
    </Stack>
  )
}

export default AiDashboardReportHistory
